/**
 * Maps category names to English-only labels
 * Handles RU/KO bilingual names and extracts English equivalents
 */

const categoryMap: Record<string, string> = {
  // Root categories
  'Электроника (전자제품)': 'Electronics',
  'Бьюти (뷰티)': 'Beauty',
  'Бытовая техника / товары для дома (생활가전)': 'Home Appliances',
  
  // Electronics children
  '휴대폰 — мобильные телефоны': 'Mobile Phones',
  '태블릿 — планшеты': 'Tablets',
  '컴퓨터, 노트북 — компьютеры и ноутбуки': 'Computers & Laptops',
  '스마트워치 — умные часы': 'Smartwatches',
  '이어폰, 스피커 — наушники и колонки': 'Earphones & Speakers',
  '카메라 — камеры': 'Cameras',
  '비디오 게임 — видеоигры / приставки': 'Video Games & Consoles',
  '프로젝터 — проекторы': 'Projectors',
  '액세서리 — аксессуары': 'Accessories',
  
  // Beauty children
  '스킨케어 — уход за кожей': 'Skincare',
  '메이크업 — макияж': 'Makeup',
  '헤어케어 — уход за волосами': 'Haircare',
  '바디케어 — уход за телом': 'Bodycare',
  '남성 뷰티 — мужская косметика': 'Men\'s Beauty',
  '손, 네일케어 — уход за руками и ногтями': 'Hand & Nail Care',
  '향수 — парфюм': 'Perfume',
  '건강식품 — БАДы / товары для здоровья': 'Health Supplements',
  '디지털 기기 — цифровые устройства': 'Digital Devices',
  '메이크업툴 — инструменты для макияжа': 'Makeup Tools',
  
  // Home children (Kitchen)
  '식기세척건조기 — посудомоечные / сушилки': 'Dishwashers & Dryers',
  '에어프라이어/튀김기 — аэрофритюрницы / фритюрницы': 'Air Fryers & Fryers',
  '전기밥솥 — рисоварки': 'Rice Cookers',
  '믹서기 — блендеры / миксеры': 'Blenders & Mixers',
  '토스터기 — тостеры': 'Toasters',
  '원액기 — соковыжималки (шнековые)': 'Slow Juicers',
  '간식메이커 — приборы для снеков': 'Snack Makers',
  '커피메이커 — кофеварки / кофемашины': 'Coffee Makers',
  '전기포트 — электрочайники': 'Electric Kettles',
  '전기그릴/팬 — электрогрили / электро-сковороды': 'Electric Grills & Pans',
  '전기쿠커 — электрокастрюли / мультиварки': 'Multi Cookers',
  '주방보조기기 — кухонные помощники': 'Kitchen Helpers',
  '계량측정기기 — измерительные приборы (весы и т.п.)': 'Measuring Devices',
  '음식물처리기 — утилизаторы/измельчители пищевых отходов': 'Food Waste Disposers',
  '다지기 — чопперы / измельчители': 'Choppers',
  
  // Home children (Living)
  '청소기 — пылесосы': 'Vacuum Cleaners',
  '공기청정기 — очистители воздуха': 'Air Purifiers',
  '안마기기 — массажёры': 'Massagers',
  '다리미 — утюги': 'Irons',
  '보풀제거기, 재봉틀 — машинки от катышков, швейные машины': 'Lint Removers & Sewing Machines',
  '면도기, 제모기 — электробритвы, эпиляторы': 'Shavers & Epilators',
  '드라이기, 고데기 — фены, стайлеры/плойки/утюжки': 'Hair Dryers & Stylers',
  '사무가전 — офисная техника': 'Office Appliances',
  '전기용품 — электропринадлежности': 'Electrical Supplies',
  '난방매트 — подогреваемые коврики/матрасы': 'Heated Mats',
  '난방기기 — обогреватели': 'Heaters',
  '냉방기기 — устройства охлаждения (вентиляторы/кондиционеры и т.п.)': 'Cooling Devices',
  '해충퇴치기 — отпугиватели насекомых': 'Insect Repellents',
  '가습기 — увлажнители воздуха': 'Humidifiers',
};

/**
 * Extracts English-only label from bilingual category name
 * @param name - Category name (may contain RU/KO text)
 * @returns English-only label
 */
export function getCategoryLabelEN(name: string): string {
  // Check direct mapping first
  if (categoryMap[name]) {
    return categoryMap[name];
  }
  
  // If name contains "—", try to extract Russian part and map
  if (name.includes('—')) {
    const parts = name.split('—');
    const russianPart = parts[1]?.trim();
    if (russianPart) {
      // Try to find by Russian part
      for (const [key, value] of Object.entries(categoryMap)) {
        if (key.includes(russianPart)) {
          return value;
        }
      }
    }
  }
  
  // Remove parentheses content (KO text)
  let cleaned = name.replace(/\([^)]*\)/g, '').trim();
  
  // Remove "—" and everything after it
  if (cleaned.includes('—')) {
    cleaned = cleaned.split('—')[0].trim();
  }
  
  // If still no match, try partial match
  for (const [key, value] of Object.entries(categoryMap)) {
    if (key.includes(cleaned) || cleaned.includes(key.split('—')[0]?.trim() || '')) {
      return value;
    }
  }
  
  // Fallback: return cleaned name (shouldn't happen with proper mapping)
  return cleaned || name;
}

